import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Col, Row, Space, Typography, Modal, Tooltip, Table, Tag } from 'antd';
import moment from 'moment';
import { CalendarOutlined, CheckCircleFilled, UserOutlined, GiftFilled } from '@ant-design/icons';
import { Storage } from 'aws-amplify';
import { usePermify } from '@permify/react-role';

import './styles.css';
import { formatCurrency2SigFig } from '../../../core/utils/campaigns';
import { CampaignRequestorTargetingRegionTable } from './CampaignRequestorTargetingRegionTable';
import { CampaignRequestorTargetingLocalTable } from './CampaignRequestorTargetingLocalTable';
import { audienceTypeMap, countries } from '../../../core/utils/constants/constants';
import S3Image from '../CampaignLauncher/components/S3Image';

const { Text } = Typography;

const CampaignRequestorReviewLineItem = ({ form, formValues, campaign, restField, name, index, fieldKey, campaignOrderStatus, hideCpmFields = false }) => {
  // Support both reactive system and old form system
  const lineItem = campaign || (form?.getFieldValue('campaigns') || [])[index] || {};
  const targetingType = lineItem.targetingType;
  const audienceType = lineItem.audienceType;
  const isRegulated = formValues ? formValues.isRegulated : form?.getFieldValue('isRegulated');

  // Get campaign type to determine if it's video or display - use parent form value first
  const campaignType = formValues?.type || form?.getFieldValue('type') || lineItem.type;
  const isVideoType = campaignType === 8 || campaignType === 'VIDEO'; // Type 8 is video/CTV

  // Permission checking for Flight Schedules visibility and Origin tag visibility
  const { isAuthorized } = usePermify();
  const [showFlightSchedules, setShowFlightSchedules] = useState(false);
  const [showOriginTag, setShowOriginTag] = useState(false);

  // Check if user can see Flight Schedules and Origin tag
  useEffect(() => {
    const checkPermissions = async () => {
      const hasAdOpsPermission = await isAuthorized(['isTrafficker', 'isProcessor'], []);

      // Origin tag: Only show to Ad Ops users (isProcessor or isTrafficker)
      setShowOriginTag(hasAdOpsPermission);

      // Flight Schedules: Hide if DRAFT status, otherwise show only to Ad Ops
      if (campaignOrderStatus === 'DRAFT') {
        setShowFlightSchedules(false);
      } else {
        setShowFlightSchedules(hasAdOpsPermission);
      }
    };

    checkPermissions();
  }, [campaignOrderStatus, isAuthorized]);

  // State for video previews
  const [previewUrls, setPreviewUrls] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalVideos, setModalVideos] = useState([]);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [lastProcessedKey, setLastProcessedKey] = useState('');

  // Get ad group creatives
  const adGroupCreatives = lineItem.adGroup?.adGroupCreatives || [];

  // Memoize all videos for processing
  const allVideos = useMemo(() => {
    return adGroupCreatives.reduce((acc, item) => acc.concat(item.medias || []), []);
  }, [adGroupCreatives]);

  // Process ad groups to get signed URLs for video previews (similar to edit mode)
  const processAdGroups = useCallback(async () => {
    if (!Array.isArray(adGroupCreatives) || adGroupCreatives.length === 0) {
      if (previewUrls.length > 0) {
        setPreviewUrls([]);
        setLastProcessedKey('');
      }
      return;
    }

    // Create a stable key to check if processing is needed
    const adGroupsKey = adGroupCreatives
      .map((ag) => `${ag.name}-${ag.medias?.length || 0}`)
      .join(',');

    // Skip processing if we've already processed this exact set
    if (adGroupsKey === lastProcessedKey) {
      return;
    }

    const processed = await Promise.all(
      adGroupCreatives.map(async (adGroup) => {
        // Process all videos in the adGroup
        const processedMedias = await Promise.all(
          (adGroup.medias || []).map(async (media) => {
            try {
              const previewUrl = media.key
                ? await Storage.get(media.key, { expires: 600000 })
                : media.url;
              return { ...media, previewUrl };
            } catch (e) {
              console.warn('Failed to get signed URL for:', media.name, e);
              return { ...media, previewUrl: media.url };
            }
          })
        );

        return { ...adGroup, medias: processedMedias };
      })
    );

    setPreviewUrls(processed);
    setLastProcessedKey(adGroupsKey);
  }, [adGroupCreatives, lastProcessedKey, previewUrls.length]);

  // Process adGroupCreatives to get signed URLs for video previews
  useEffect(() => {
    if (isVideoType) {
      processAdGroups();
    }
  }, [isVideoType, processAdGroups]);

  // Video Thumbnail Component for preview
  const VideoThumbnail = ({ media, onClick }) => (
    <div
      onClick={onClick}
      style={{
        width: 40,
        height: 40,
        cursor: 'pointer',
        border: '1px solid #d9d9d9',
        borderRadius: 4,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        position: 'relative',
      }}
    >
      {media?.previewUrl ? (
        <>
          <video
            src={media.previewUrl}
            type="video/mp4"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            muted
            preload="metadata"
          />
          {/* Play icon overlay */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '12px',
              color: 'white',
              textShadow: '0 0 4px rgba(0,0,0,0.5)',
            }}
          >
            ▶
          </div>
        </>
      ) : (
        <Text type="secondary" style={{ fontSize: 10 }}>
          No Preview
        </Text>
      )}
    </div>
  );

  // Enhanced Video Carousel Modal Component
  const VideoCarouselModal = ({ videos, visible, onClose, initialIndex = 0 }) => {
    const [currentVideoIndex, setCurrentVideoIndex] = useState(initialIndex);

    useEffect(() => {
      setCurrentVideoIndex(initialIndex);
    }, [initialIndex]);

    const currentVideo = videos[currentVideoIndex];

    return (
      <Modal
        open={visible}
        title={`Video Player (${currentVideoIndex + 1} of ${videos.length})`}
        onCancel={onClose}
        footer={null}
        width={900}
        style={{ top: 20 }}
      >
        <div style={{ marginBottom: 16 }}>
          {/* Main Video Player */}
          {currentVideo?.previewUrl && (
            <video
              key={currentVideo.previewUrl} // Force re-render when video changes
              controls
              width="100%"
              style={{ maxHeight: '50vh', backgroundColor: '#000' }}
              autoPlay={false}
            >
              <source src={currentVideo.previewUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}

          {/* Video Info */}
          <div style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
            <Text strong>{currentVideo?.name}</Text>
            {currentVideo?.fileType && (
              <Text type="secondary" style={{ marginLeft: 8 }}>
                ({currentVideo.fileType})
              </Text>
            )}
          </div>
        </div>

        {/* Thumbnail Strip */}
        {videos.length > 1 && (
          <div>
            <Text strong style={{ marginBottom: 8, display: 'block' }}>
              Select Video ({videos.length} videos):
            </Text>
            <div
              style={{
                display: 'flex',
                gap: 8,
                overflowX: 'auto',
                padding: '8px 0',
                maxHeight: '120px',
              }}
            >
              {videos.map((video, index) => (
                <div
                  key={index}
                  onClick={() => setCurrentVideoIndex(index)}
                  style={{
                    width: 100,
                    height: 75,
                    cursor: 'pointer',
                    border: currentVideoIndex === index ? '2px solid #1890ff' : '1px solid #d9d9d9',
                    borderRadius: 4,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    position: 'relative',
                    flexShrink: 0,
                  }}
                >
                  {video.previewUrl ? (
                    <>
                      <video
                        src={video.previewUrl}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        muted
                        preload="metadata"
                      />
                      {/* Play icon overlay */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontSize: '16px',
                          color: 'white',
                          textShadow: '0 0 4px rgba(0,0,0,0.8)',
                        }}
                      >
                        {currentVideoIndex === index ? '⏸' : '▶'}
                      </div>
                      {/* Video number badge */}
                      <div
                        style={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          backgroundColor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          fontSize: '10px',
                          padding: '1px 4px',
                          borderRadius: 2,
                        }}
                      >
                        {index + 1}
                      </div>
                    </>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      No Preview
                    </Text>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    );
  };


  // Line item indicator tags
  const lineItemTitle = (
    <Space size="middle" align="center">
      <span>LINE ITEM {index + 1}</span>
      {showOriginTag && lineItem.isOrigin && campaignOrderStatus !== 'DRAFT' && (
        <Tooltip title="Client Created Line Item">
          <Tag color="blue" style={{ margin: 0 }}>
            <UserOutlined style={{ marginRight: 4 }} />
            Client
          </Tag>
        </Tooltip>
      )}
      {lineItem.markedForDelivery && (
        <Tooltip title="Marked for Delivery">
          <Tag color="green" style={{ margin: 0 }}>
            <CheckCircleFilled style={{ marginRight: 4 }} />
            Delivery
          </Tag>
        </Tooltip>
      )}
      {lineItem.valueAdd && (
        <Tooltip title="Value Added Line Item">
          <Tag color="purple" style={{ margin: 0 }}>
            <GiftFilled style={{ marginRight: 4 }} />
            Value Add
          </Tag>
        </Tooltip>
      )}
    </Space>
  );

  return (
    <Card title={lineItemTitle}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Row gutter={20}>
          <Col span={6}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text type="secondary">Line Item Name</Text>
              <Text>{lineItem.name}</Text>
            </Space>
          </Col>
          <Col span={6}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text type="secondary">Start Date</Text>
              <Text>{moment(lineItem.startDate).format('MM/DD/YYYY')}</Text>
            </Space>
          </Col>
          <Col span={6}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text type="secondary">End Date</Text>
              <Text>{moment(lineItem.endDate).format('MM/DD/YYYY')}</Text>
            </Space>
          </Col>
        </Row>
        <Row gutter={20}>
          <Col span={6}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text type="secondary">Line Item Budget</Text>
              <Text>{formatCurrency2SigFig(lineItem.budgetTotal)}</Text>
            </Space>
          </Col>
          {!hideCpmFields && (
            <>
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">CPM</Text>
                  <Text>{lineItem.cpm}</Text>
                </Space>
              </Col>
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">Impressions</Text>
                  <Text>{(lineItem.impressions || 0).toLocaleString()}</Text>
                </Space>
              </Col>
            </>
          )}
        </Row>

        {/* Flight Schedules Section - View Only - Hidden if DRAFT or user lacks permissions */}
        {showFlightSchedules && lineItem.flightSchedules && lineItem.flightSchedules.length > 0 && (
          <Row gutter={20}>
            <Col span={24}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  <CalendarOutlined style={{ marginRight: 8 }} />
                  FLIGHT SCHEDULES
                </Text>
                <Table
                  dataSource={lineItem.flightSchedules.map((flight, idx) => ({
                    ...flight,
                    key: flight.id || idx,
                  }))}
                  columns={[
                    {
                      title: 'Flight',
                      key: 'flight',
                      width: 80,
                      render: (_, __, idx) => (
                        <Tag color="blue">Flight {idx + 1}</Tag>
                      ),
                    },
                    {
                      title: 'Start Date',
                      dataIndex: 'startDate',
                      key: 'startDate',
                      render: (date) => date ? moment(date).format('MM/DD/YYYY') : '-',
                    },
                    {
                      title: 'End Date',
                      dataIndex: 'endDate',
                      key: 'endDate',
                      render: (date) => date ? moment(date).format('MM/DD/YYYY') : '-',
                    },
                    {
                      title: 'Budget',
                      dataIndex: 'budget',
                      key: 'budget',
                      render: (budget) => budget ? formatCurrency2SigFig(budget) : '-',
                    },
                    // CPM column - hidden for Percentage pricing
                    ...(!hideCpmFields ? [{
                      title: 'CPM',
                      dataIndex: 'cpm',
                      key: 'cpm',
                      render: (cpm) => cpm ? `$${cpm}` : '-',
                    }] : []),
                  ]}
                  pagination={false}
                  size="small"
                  style={{ marginTop: 8 }}
                />
                <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
                  Total: {lineItem.flightSchedules.length} flight schedule(s)
                </div>
              </Space>
            </Col>
          </Row>
        )}

        {targetingType === 'REGION' && (
          <CampaignRequestorTargetingRegionTable
            name={name}
            restField={restField}
            fieldKey={fieldKey}
            form={form}
            index={index}
          />
        )}
        {targetingType === 'LOCAL' && (
          <CampaignRequestorTargetingLocalTable
            name={name}
            restField={restField}
            fieldKey={fieldKey}
            form={form}
            index={index}
          />
        )}
        {/* Enhanced Targeting Section */}
        <Row gutter={20}>
          <Col span={24}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: '16px', fontWeight: 'bold' }}>
                TARGETING: {targetingType?.toUpperCase() || 'NOT SET'}
              </Text>
              {targetingType === 'COUNTRY' && (
                <Text style={{ fontSize: '14px' }}>
                  {lineItem.targetingCountries
                    ? (typeof lineItem.targetingCountries === 'string'
                        ? countries.find(c => c.code === lineItem.targetingCountries)?.name || lineItem.targetingCountries
                        : lineItem.targetingCountries.name || lineItem.targetingCountries.code)
                    : 'Not selected'}
                </Text>
              )}
              {targetingType === 'REGION' && (
                <Text style={{ fontSize: '14px' }}>
                  {lineItem.targetingRegions?.length > 0
                    ? `${lineItem.targetingRegions.length} regions selected`
                    : 'No regions selected'}
                </Text>
              )}
              {targetingType === 'LOCAL' && (
                <Text style={{ fontSize: '14px' }}>
                  {lineItem.targetingLocations?.length > 0
                    ? `${lineItem.targetingLocations.length} locations selected`
                    : 'No locations selected'}
                </Text>
              )}
            </Space>
          </Col>
        </Row>

        {/* Enhanced Audience Section */}
        <Row gutter={20}>
          <Col span={24}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                Audience Type
              </Text>
              <Text style={{ fontSize: '14px' }}>
                {audienceTypeMap[audienceType] || 'All users in targeting area'} {isRegulated ? 'aged 21+' : ''}
              </Text>
            </Space>
          </Col>
        </Row>

        {/* First Party Audiences Section */}
        {lineItem.firstPartyDataAudiences && lineItem.firstPartyDataAudiences.length > 0 && (
          <Row gutter={20}>
            <Col span={24}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  FIRST PARTY AUDIENCES
                </Text>
                {lineItem.firstPartyDataAudiences.map((audience, audienceIndex) => (
                  <div key={audienceIndex} style={{ marginLeft: 16 }}>
                    <Text style={{ fontSize: '14px' }}>• {audience.name}</Text>
                  </div>
                ))}
              </Space>
            </Col>
          </Row>
        )}

        {/* Ad Groups Section */}
        {lineItem.adGroup && lineItem.adGroup.adGroupCreatives && lineItem.adGroup.adGroupCreatives.length > 0 && (
          <Row gutter={20}>
            <Col span={24}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  AD GROUP
                </Text>
                <Text type="secondary" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  {isVideoType ? 'VIDEO CREATIVES' : 'DISPLAY AD GROUPS'}
                </Text>

                {/* Ad Groups Table */}
                <div style={{ marginTop: 10 }}>
                  <Row gutter={20} style={{ marginBottom: 8, fontWeight: 'bold', fontSize: '12px' }}>
                    <Col span={6}>{isVideoType ? 'Video Creative Name' : 'Display Ad Group Name'}</Col>
                    <Col span={6}>Click-Through Link</Col>
                    <Col span={4}>Duration</Col>
                    <Col span={8}>{isVideoType ? 'Preview' : 'Gallery'}</Col>
                  </Row>

                  {lineItem.adGroup.adGroupCreatives.map((creative, creativeIndex) => (
                    <Row key={creativeIndex} gutter={20} style={{ marginBottom: 12, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <Col span={6}>
                        <Text style={{ fontSize: '14px' }}>{creative.name || `Creative ${creativeIndex + 1}`}</Text>
                      </Col>
                      <Col span={6}>
                        <Text style={{ fontSize: '14px' }}>{creative.clickthrough || 'Not specified'}</Text>
                      </Col>
                      <Col span={4}>
                        <Text style={{ fontSize: '14px' }}>
                          {creative.startDate && creative.endDate
                            ? `${moment(creative.startDate).format('MM/DD/YYYY')} - ${moment(creative.endDate).format('MM/DD/YYYY')}`
                            : 'Not specified'
                          }
                        </Text>
                      </Col>
                      <Col span={8}>
                        {creative.medias && creative.medias.length > 0 ? (
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {isVideoType ? (
                              // Video preview for video campaigns
                              (() => {
                                // Find the matching processed ad group for this creative
                                const processedAdGroup = previewUrls.find(pg => pg.name === creative.name);
                                const videos = processedAdGroup?.medias || creative.medias;

                                return (
                                  <>
                                    {videos.slice(0, 1).map((media, mediaIndex) => (
                                      <VideoThumbnail
                                        key={mediaIndex}
                                        media={media}
                                        onClick={() => {
                                          // Open carousel modal starting with first video
                                          setModalVideos(videos);
                                          setSelectedVideoIndex(0);
                                          setModalVisible(true);
                                        }}
                                      />
                                    ))}
                                    {videos.length > 1 && (
                                      <Tooltip title={`View all ${videos.length} videos`}>
                                        <div
                                          style={{
                                            width: 40,
                                            height: 40,
                                            border: '1px dashed #d9d9d9',
                                            borderRadius: 4,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#fafafa',
                                            cursor: 'pointer',
                                          }}
                                          onClick={() => {
                                            // Open carousel modal with all videos
                                            setModalVideos(videos);
                                            setSelectedVideoIndex(0);
                                            setModalVisible(true);
                                          }}
                                        >
                                          <Text type="secondary" style={{ fontSize: 10 }}>
                                            +{videos.length - 1}
                                          </Text>
                                        </div>
                                      </Tooltip>
                                    )}
                                  </>
                                );
                              })()
                            ) : (
                              // Display images for display campaigns
                              <>
                                {creative.medias.slice(0, 3).map((media, mediaIndex) => (
                                  <div key={mediaIndex} style={{
                                    width: '40px',
                                    height: '40px',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <S3Image
                                      imgKey={media.key}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                  </div>
                                ))}
                                {creative.medias.length > 3 && (
                                  <Text style={{ fontSize: '12px', alignSelf: 'center' }}>
                                    +{creative.medias.length - 3} more
                                  </Text>
                                )}
                              </>
                            )}
                          </div>
                        ) : (
                          <Text style={{ fontSize: '14px', color: '#999' }}>No media selected</Text>
                        )}
                      </Col>
                    </Row>
                  ))}

                  <div style={{ marginTop: 12, fontSize: '14px', fontWeight: 'bold' }}>
                    {lineItem.adGroup.adGroupCreatives.length} {isVideoType ? 'Video Creative Group(s)' : 'Display Ad Group(s)'} | {
                      lineItem.adGroup.adGroupCreatives.reduce((total, creative) =>
                        total + (creative.medias ? creative.medias.length : 0), 0
                      )
                    } {isVideoType ? 'Video Creative(s)' : 'Display Ad Unit(s)'}
                  </div>
                </div>
              </Space>
            </Col>
          </Row>
        )}

        {/* Show message if no ad groups */}
        {(!lineItem.adGroup || !lineItem.adGroup.adGroupCreatives || lineItem.adGroup.adGroupCreatives.length === 0) && (
          <Row gutter={20}>
            <Col span={24}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  AD GROUP
                </Text>
                <Text type="secondary" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  {isVideoType ? 'VIDEO CREATIVES' : 'DISPLAY AD GROUPS'}
                </Text>
                <Text style={{ fontSize: '14px', color: '#999' }}>
                  {isVideoType ? 'No Video Creatives Selected' : 'No Ad Groups Selected'}
                </Text>
              </Space>
            </Col>
          </Row>
        )}

        {/* Video Carousel Modal */}
        <VideoCarouselModal
          videos={modalVideos}
          visible={modalVisible}
          onClose={() => {
            setModalVisible(false);
            setModalVideos([]);
            setSelectedVideoIndex(0);
          }}
          initialIndex={selectedVideoIndex}
        />
      </Space>
    </Card>
  );
};

export default CampaignRequestorReviewLineItem;
